import React from 'react'
import { ChevronDownIcon } from '@heroicons/react/20/solid' // Ensure you have this icon installed
import classNames from 'classnames'

export interface DropdownOption<T extends string> {
  id: string
  value: T
  label: string
  disabled?: boolean
}

interface DropdownProps<T extends string> {
  label?: string
  value: DropdownOption<T>
  setValue: (option: DropdownOption<T>) => void
  options: DropdownOption<T>[]
  className?: string
  icon?: React.ReactNode
  textSize?: 'text-xs' | 'text-sm' | 'text-base' | 'text-lg' | 'text-xl'
  disabled?: boolean
  defaultValue?: DropdownOption<T>
}

export const Dropdown = <T extends string>({
  label,
  value,
  setValue,
  options,
  className,
  icon,
  textSize = 'text-xs',
  disabled,
  defaultValue,
}: DropdownProps<T>) => {
  return (
    <div
      className={`relative flex w-full flex-col items-start justify-start space-y-2 ${className}`}
    >
      {label && (
        <label
          htmlFor="dropdown"
          className="block flex-1 text-xs font-medium uppercase text-gray-500"
        >
          {label}
        </label>
      )}
      <div className="flex w-full flex-1 items-center justify-between">
        <div className="relative flex w-full items-center">
          {/* Display the selected option with the icon and chevron */}
          <div
            className={classNames(
              'flex w-full items-center space-x-2 rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10',
              disabled && 'pointer-events-none bg-gray-100'
            )}
          >
            {icon && <span>{icon}</span>}
            <span className={textSize}>{value.label}</span>
            <ChevronDownIcon className="pointer-events-none absolute right-2 h-4 w-4 text-gray-400" />
          </div>
          {/* Actual select dropdown for options */}
          <select
            disabled={disabled}
            id="dropdown"
            className={classNames(
              'absolute inset-0 w-full cursor-pointer opacity-0',
              disabled && 'pointer-events-none'
            )}
            defaultValue={defaultValue?.value}
            value={value.value}
            onChange={(e) =>
              setValue(
                options.find(
                  (o) => o.value === e.target.value
                ) as DropdownOption<T>
              )
            }
          >
            {options.map((o) => (
              <option key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}
